import type {
  IamAdministrationAddGroupBodyDto,
  IamAdministrationAddPlatformRoleBodyDto,
  IamAdministrationAddRolePermissionBodyDto,
  IamAdministrationBulkAddUsersToGroupBodyDto,
  IamAdministrationBulkRemoveUsersFromGroupBodyDto,
  IamAdministrationCatalogDto,
  IamAdministrationCreateGroupBodyDto,
  IamAdministrationCreatePolicyBindingBodyDto,
  IamAdministrationCreateRoleBodyDto,
  IamAdministrationGroupRowDto,
  IamAdministrationPatchRoleBodyDto,
  IamAdministrationPermissionRefDto,
  IamAdministrationPolicyBindingRowDto,
  IamAdministrationRolePermissionsDto,
  IamAdministrationRoleRowDto,
  IamAdministrationUserDetailDto,
  IamAdministrationUserSummaryDto,
} from '@act/shared/data-transfer-objects';
import { EndpointDefinition } from '../utils/endpointDefinition';

export const iamRequests = {
  listUsers: EndpointDefinition.get<IamAdministrationUserSummaryDto[], null>({
    name: 'iamListUsers',
    route: '/users/administration/iam/users',
  }),
  getUserDetail: EndpointDefinition.get<
    IamAdministrationUserDetailDto,
    { principalId: string }
  >({
    name: 'iamGetUserDetail',
    route: '/users/administration/iam/users/:principalId',
  }),
  getCatalog: EndpointDefinition.get<IamAdministrationCatalogDto, null>({
    name: 'iamGetCatalog',
    route: '/users/administration/iam/catalog',
  }),
  listPolicies: EndpointDefinition.get<IamAdministrationPolicyBindingRowDto[], null>({
    name: 'iamListPolicies',
    route: '/users/administration/iam/policies',
  }),
  createPolicyBinding: EndpointDefinition.post<
    IamAdministrationPolicyBindingRowDto,
    IamAdministrationCreatePolicyBindingBodyDto,
    null
  >({
    name: 'iamCreatePolicyBinding',
    route: '/users/administration/iam/policies',
  }),
  deletePolicyBinding: EndpointDefinition.delete<void, { bindingId: string }>({
    name: 'iamDeletePolicyBinding',
    route: '/users/administration/iam/policies/:bindingId',
  }),
  listGroups: EndpointDefinition.get<IamAdministrationGroupRowDto[], null>({
    name: 'iamListGroups',
    route: '/users/administration/iam/groups',
  }),
  createGroup: EndpointDefinition.post<
    IamAdministrationGroupRowDto,
    IamAdministrationCreateGroupBodyDto,
    null
  >({
    name: 'iamCreateGroup',
    route: '/users/administration/iam/groups',
  }),
  deleteGroup: EndpointDefinition.delete<void, { principalId: string }>({
    name: 'iamDeleteGroup',
    route: '/users/administration/iam/groups/:principalId',
  }),
  bulkAddUsersToGroup: EndpointDefinition.post<
    IamAdministrationGroupRowDto,
    IamAdministrationBulkAddUsersToGroupBodyDto,
    { principalId: string }
  >({
    name: 'iamBulkAddUsersToGroup',
    route: '/users/administration/iam/groups/:principalId/members/bulk',
  }),
  bulkRemoveUsersFromGroup: EndpointDefinition.post<
    IamAdministrationGroupRowDto,
    IamAdministrationBulkRemoveUsersFromGroupBodyDto,
    { principalId: string }
  >({
    name: 'iamBulkRemoveUsersFromGroup',
    route: '/users/administration/iam/groups/:principalId/members/bulk-remove',
  }),
  listPermissions: EndpointDefinition.get<IamAdministrationPermissionRefDto[], null>(
    {
      name: 'iamListPermissions',
      route: '/users/administration/iam/permissions',
    }
  ),
  listRoles: EndpointDefinition.get<IamAdministrationRoleRowDto[], null>({
    name: 'iamListRoles',
    route: '/users/administration/iam/roles',
  }),
  createRole: EndpointDefinition.post<
    IamAdministrationRoleRowDto,
    IamAdministrationCreateRoleBodyDto,
    null
  >({
    name: 'iamCreateRole',
    route: '/users/administration/iam/roles',
  }),
  patchRole: EndpointDefinition.patch<
    IamAdministrationRoleRowDto,
    IamAdministrationPatchRoleBodyDto,
    { roleId: string }
  >({
    name: 'iamPatchRole',
    route: '/users/administration/iam/roles/:roleId',
  }),
  deleteRole: EndpointDefinition.delete<void, { roleId: string }>({
    name: 'iamDeleteRole',
    route: '/users/administration/iam/roles/:roleId',
  }),
  getRolePermissions: EndpointDefinition.get<
    IamAdministrationRolePermissionsDto,
    { roleId: string }
  >({
    name: 'iamGetRolePermissions',
    route: '/users/administration/iam/roles/:roleId/permissions',
  }),
  addRolePermission: EndpointDefinition.post<
    IamAdministrationRolePermissionsDto,
    IamAdministrationAddRolePermissionBodyDto,
    { roleId: string }
  >({
    name: 'iamAddRolePermission',
    route: '/users/administration/iam/roles/:roleId/permissions',
  }),
  removeRolePermission: EndpointDefinition.delete<
    IamAdministrationRolePermissionsDto,
    { roleId: string; permissionId: string }
  >({
    name: 'iamRemoveRolePermission',
    route:
      '/users/administration/iam/roles/:roleId/permissions/:permissionId',
  }),
  addUserGroup: EndpointDefinition.post<
    IamAdministrationUserDetailDto,
    IamAdministrationAddGroupBodyDto,
    { principalId: string }
  >({
    name: 'iamAddUserGroup',
    route: '/users/administration/iam/users/:principalId/groups',
  }),
  removeUserGroup: EndpointDefinition.delete<
    IamAdministrationUserDetailDto,
    { principalId: string; groupPrincipalId: string }
  >({
    name: 'iamRemoveUserGroup',
    route:
      '/users/administration/iam/users/:principalId/groups/:groupPrincipalId',
  }),
  addPlatformBinding: EndpointDefinition.post<
    IamAdministrationUserDetailDto,
    IamAdministrationAddPlatformRoleBodyDto,
    { principalId: string }
  >({
    name: 'iamAddPlatformBinding',
    route: '/users/administration/iam/users/:principalId/platform-bindings',
  }),
  removePlatformBinding: EndpointDefinition.delete<
    IamAdministrationUserDetailDto,
    { principalId: string; bindingId: string }
  >({
    name: 'iamRemovePlatformBinding',
    route:
      '/users/administration/iam/users/:principalId/platform-bindings/:bindingId',
  }),
};
